// The 3D-web course island: the workstation model sitting on the platform,
// with a wireframe globe turning above it — the course is about putting 3D on
// the web, so the scene says so.
import React, { useMemo, useRef, useState } from 'react';
import { Float } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { Fitted, HaloRing, PLATFORM_TOP, ShadowBlob, useCloned, usePointerCursor } from './helpers';

/** Set to Math.PI if the model ends up facing away from the camera. */
const FACING = 0;

/** Wireframe globe + a tilted orbit ring. Rendered unclamped so the bloom pass
    picks it up as a glow rather than flat colour. */
const Globe = ({ accent, reduced }) => {
  const group = useRef();
  const shell = useRef();
  const ring = useRef();
  const light = useRef();
  const burst = useRef(0);
  const [hovered, setHovered] = useState(false);
  const [reducedTurn, setReducedTurn] = useState(0);
  usePointerCursor(hovered);

  useFrame((state, delta) => {
    if (!group.current) return;

    if (reduced) {
      group.current.rotation.y = reducedTurn * (Math.PI / 2);
      group.current.scale.setScalar(hovered ? 1.06 : 1);
      if (ring.current) ring.current.scale.setScalar(1);
      if (light.current) light.current.intensity = hovered ? 1.9 : 1.4;
      return;
    }

    let surge = 0;
    if (burst.current > 0) {
      const progress = 1 - burst.current / 1.12;
      surge = Math.sin(progress * Math.PI);
      burst.current = Math.max(0, burst.current - delta);
    }

    if (shell.current) shell.current.rotation.y += delta * (0.28 + surge * 5.2);
    if (ring.current) {
      ring.current.rotation.z += delta * (0.2 + surge * 4.4);
      ring.current.rotation.x += delta * (0.08 + surge * 1.5);
      ring.current.scale.setScalar(THREE.MathUtils.damp(ring.current.scale.x, 1 + surge * 0.3, 12, delta));
    }
    const targetScale = 1 + (hovered ? 0.08 : 0) + surge * 0.14;
    group.current.scale.setScalar(THREE.MathUtils.damp(group.current.scale.x, targetScale, 12, delta));
    group.current.rotation.z = Math.sin(state.clock.elapsedTime * 0.8) * surge * 0.08;
    if (light.current) light.current.intensity = 1.4 + (hovered ? 0.5 : 0) + surge * 3.4;
  });

  const activate = (event) => {
    event.stopPropagation();
    if (reduced) {
      setReducedTurn((turn) => (turn + 1) % 4);
    } else if (burst.current <= 0) {
      burst.current = 1.12;
    }
  };

  return (
    <group
      ref={group}
      onPointerOver={(event) => { event.stopPropagation(); setHovered(true); }}
      onPointerOut={() => setHovered(false)}
      onClick={activate}
    >
      <mesh ref={shell}>
        <icosahedronGeometry args={[0.62, 2]} />
        <meshBasicMaterial color={accent} wireframe transparent opacity={0.6} toneMapped={false} />
      </mesh>
      <mesh ref={ring} rotation={[Math.PI / 2.4, 0, 0]}>
        <torusGeometry args={[0.95, 0.012, 10, 80]} />
        <meshBasicMaterial color={accent} transparent opacity={0.55} toneMapped={false} />
      </mesh>
      <pointLight ref={light} color={accent} intensity={1.4} distance={4} decay={2} />
    </group>
  );
};

/** Data motes drifting up from the workstation into the globe. */
const Uplink = ({ accent, reduced, height }) => {
  const group = useRef();
  const motes = useMemo(
    () =>
      new Array(9).fill(0).map((_, i) => ({
        key: i,
        x: (Math.random() - 0.5) * 1.5,
        z: (Math.random() - 0.5) * 1.2,
        offset: Math.random(),
        scale: 0.022 + Math.random() * 0.022,
      })),
    []
  );

  useFrame(({ clock }) => {
    if (reduced || !group.current) return;
    const t = clock.getElapsedTime();
    group.current.children.forEach((mote, i) => {
      // Each mote loops from the deck up to the globe on its own phase.
      const progress = ((t * 0.28 + motes[i].offset) % 1);
      mote.position.y = progress * height;
      mote.material.opacity = Math.sin(progress * Math.PI) * 0.85;
    });
  });

  return (
    <group ref={group}>
      {motes.map(({ key, x, z, scale }) => (
        <mesh key={key} position={[x, 0, z]} scale={scale}>
          <octahedronGeometry args={[1, 0]} />
          <meshBasicMaterial
            color={accent}
            transparent
            opacity={0}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
            toneMapped={false}
          />
        </mesh>
      ))}
    </group>
  );
};

const Web3DIsland = ({ accent, accentInk, reduced }) => {
  const platform = useCloned('/GodotBotPlatform.glb');
  const model = useCloned('/Web3dModel.glb');
  const [top, setTop] = useState(PLATFORM_TOP);

  const globeHeight = 2.9;
  const globe = <Globe accent={accent} reduced={reduced} />;

  return (
    <group>
      <Fitted object={platform} size={5} fit="max" onFit={({ height }) => setTop(height)} />
      <ShadowBlob radius={3.1} opacity={0.55} position={[0, top + 0.012, 0]} />
      <HaloRing accent={accent} accentInk={accentInk} radius={2.5} position={[0, top - 0.4, 0]} />

      {/* Wide and flat (1.0 x 0.42 x 0.84), so it's sized by its widest axis —
          fitting by height would blow it up past the platform. */}
      <group position={[0, top, 0]} rotation={[0, FACING, 0]}>
        <Fitted object={model} size={3.8} fit="max" />
      </group>

      {/* Screen glow, so the model isn't lit only from outside. */}
      <pointLight
        color={accent}
        intensity={1.6}
        distance={4.5}
        decay={2}
        position={[0, top + 0.9, 0.6]}
      />

      <group position={[0, top + 0.4, 0]}>
        <Uplink accent={accent} reduced={reduced} height={globeHeight - 0.4} />
      </group>

      <group position={[0, top + globeHeight, 0]}>
        {reduced ? (
          globe
        ) : (
          <Float speed={1.2} rotationIntensity={0.2} floatIntensity={0.5} floatingRange={[0, 0.16]}>
            {globe}
          </Float>
        )}
      </group>
    </group>
  );
};

export default Web3DIsland;
